import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Heart, MapPin, Loader2, Camera, Send, X,
  Trophy, Sparkles, Users,
} from "lucide-react";
import { api } from "@/lib/api";
import PhotoUploader from "@/components/PhotoUploader";

/**
 * /missions — public list of active community missions.
 *
 * - Settings + active list pulled from /api/missions/* . When
 *   missions_enabled is false, list is empty and we render the closed
 *   message inline (no redirect — page is still useful as a landing).
 * - Submit-proof flow opens a modal with selfie uploader + caption +
 *   optional one-tap location capture (browser geolocation, opt-in).
 * - Requires login to submit; logged-out hunters see "Sign in to submit"
 *   button that bounces to /login?next=/missions/{id}.
 */
export default function MissionsPage() {
  const [s, setS] = useState(null);
  const [missions, setMissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState(null);  // mission opened in submit modal
  const [me, setMe] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/missions/settings").then(({ data }) => { if (!cancelled) setS(data || {}); }).catch(() => { if (!cancelled) setS({}); });
    api.get("/missions/active").then(({ data }) => { if (!cancelled) setMissions(data?.missions || []); }).catch(() => { if (!cancelled) setMissions([]); });
    api.get("/missions/stats").then(({ data }) => { if (!cancelled) setStats(data); }).catch(() => { if (!cancelled) setStats(null); });
    api.get("/auth/me").then(({ data }) => { if (!cancelled) setMe(data?.user || null); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!s) {
    return (
      <div className="splash-root flex items-center justify-center" data-testid="missions-page-loading">
        <div className="splash-mono text-sm opacity-60">Loading…</div>
      </div>
    );
  }

  const enabled = s.missions_enabled !== false;
  const weeklys = missions.filter((m) => m.type === "weekly");
  const evergreens = missions.filter((m) => m.type !== "weekly");

  return (
    <div className="splash-root" data-testid="missions-page">
      <section className="splash-hero relative">
        {s.page_hero_image_url && (
          <div className="splash-hero-bg" style={{ backgroundImage: `url(${s.page_hero_image_url})` }} aria-hidden="true" />
        )}
        <div className="splash-hero-overlay" aria-hidden="true" />
        <div className="splash-grain" aria-hidden="true" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-16 md:pb-24">
          <Link to="/" className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A] inline-flex items-center gap-2 hover:text-[#84d130]">
            <ArrowLeft size={12} /> Back to The Local Loot
          </Link>
          <div className="mt-8 splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
            {s.page_overline || "Community missions"}
          </div>
          <h1 className="splash-display text-5xl md:text-7xl uppercase mt-3 leading-[0.95]">
            <span className="block headline-stamp">{(s.page_headline || "Do good. Earn the badge.").split(".")[0]}.</span>
            <span className="block headline-stamp--green mt-1 md:mt-2">
              {(s.page_headline || "").split(".").slice(1).join(".").trim() || "Earn the badge."}
            </span>
          </h1>
          {s.page_intro && (
            <p className="mt-5 max-w-2xl text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed">
              {s.page_intro}
            </p>
          )}
          {stats && (
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <span className="live-chip-light">
                <Heart size={12} /> {stats.good_deeds_total} {s.ticker_total_label || "good deeds done"}
              </span>
              {stats.loot_legends_total > 0 && (
                <span className="live-chip-light" style={{ borderColor: "rgba(215,166,42,0.55)", background: "rgba(215,166,42,0.12)", color: "#d7a62a" }}>
                  <Trophy size={12} /> {stats.loot_legends_total} {s.ticker_legends_label || "Loot Legends crowned"}
                </span>
              )}
              <Link to="/good-deeds" className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A] hover:text-[#84d130] inline-flex items-center gap-1">
                {s.ticker_view_all_label || "See the wall"} <ArrowRight size={12} />
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="cream-section py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {!enabled ? (
            <div className="text-center max-w-2xl mx-auto py-12">
              <Sparkles size={40} className="mx-auto text-[#76B82A] opacity-60" />
              <h2 className="splash-display text-2xl md:text-3xl uppercase mt-4">Missions paused this week.</h2>
              <p className="mt-3 text-[15px] text-[#0d100d]/75 leading-relaxed">
                {s.closed_message || "Community missions are paused. Check back soon!"}
              </p>
            </div>
          ) : missions.length === 0 ? (
            <div className="text-center max-w-2xl mx-auto py-10">
              <Sparkles size={36} className="mx-auto text-[#76B82A] opacity-50" />
              <h2 className="splash-display text-2xl uppercase mt-3">No active missions right now.</h2>
              <p className="mt-2 text-[14px] text-[#0d100d]/65">Follow us on social to catch the next one.</p>
            </div>
          ) : (
            <>
              {weeklys.length > 0 && (
                <div className="space-y-6">
                  <h2 className="splash-display text-3xl md:text-5xl uppercase">This week</h2>
                  {weeklys.map((m) => (
                    <MissionCard key={m.id} m={m} me={me} onSubmit={() => setActive(m)} primary />
                  ))}
                </div>
              )}
              {evergreens.length > 0 && (
                <div className="mt-12 space-y-5">
                  <h2 className="splash-display text-2xl md:text-3xl uppercase">Always on</h2>
                  <div className="grid md:grid-cols-2 gap-5">
                    {evergreens.map((m) => (
                      <MissionCard key={m.id} m={m} me={me} onSubmit={() => setActive(m)} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <footer className="splash-footer py-8 text-center">
        <div className="max-w-7xl mx-auto px-4 md:px-8 splash-mono text-[12px] text-[#f7f3e6]/55">
          © The Local Loot · Live Life. Do Good. Be Happy.
        </div>
      </footer>

      {active && (
        <ProofModal
          mission={active}
          settings={s}
          me={me}
          onClose={(submitted) => {
            setActive(null);
            if (submitted) {
              api.get("/missions/active").then(({ data }) => setMissions(data?.missions || []));
              api.get("/missions/stats").then(({ data }) => setStats(data));
            }
          }}
        />
      )}
    </div>
  );
}

/* ===================================================================== */

function MissionCard({ m, me, onSubmit, primary }) {
  const remaining = m.max_completions ? Math.max(0, m.max_completions - (m.completion_count || 0)) : null;
  // Non-primary (evergreen) cards render the image as a true edge-to-edge
  // banner above the body. We strip the card's own padding and apply it
  // back to the inner content block so nothing else shifts.
  const showBanner = !primary && !!m.image_url;
  return (
    <article
      className={`bg-[#fffaef] border-2 border-[#0d100d] ${primary ? "shadow-[8px_8px_0_#0d100d] p-5 md:p-7 grid md:grid-cols-12 gap-6" : `shadow-[4px_4px_0_#0d100d] ${showBanner ? "" : "p-5 md:p-7"}`}`}
      data-testid={`mission-card-${m.id}`}
    >
      {primary && m.image_url && (
        <div className="md:col-span-5">
          <div
            className="w-full aspect-[4/3] bg-cover bg-center bg-no-repeat border-2 border-[#0d100d]"
            style={{ backgroundImage: `url(${m.image_url})` }}
            aria-label={m.title}
            data-testid={`mission-image-${m.id}`}
          />
        </div>
      )}
      {showBanner && (
        <div
          className="block w-full aspect-[16/9] bg-cover bg-center bg-no-repeat border-b-2 border-[#0d100d]"
          style={{ backgroundImage: `url(${m.image_url})` }}
          role="img"
          aria-label={m.title}
          data-testid={`mission-image-${m.id}`}
        />
      )}
      <div className={primary ? "md:col-span-7" : (showBanner ? "p-5 md:p-7" : "")}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="splash-mono uppercase tracking-[0.22em] text-[10px] text-[#4f8c19] bg-[#76B82A]/15 border border-[#76B82A]/40 px-2 py-0.5 rounded-full">
            {m.type === "weekly" ? "This week" : "Always on"}
          </span>
          {m.city && (
            <span className="inline-flex items-center gap-1 text-[12px] text-[#0d100d]/55">
              <MapPin size={11} /> {m.city}
            </span>
          )}
          {m.sponsor && (
            <span className="text-[12px] text-[#0d100d]/65">
              · backed by <span className="font-semibold text-[#0d100d]">{m.sponsor.business_name}</span>
            </span>
          )}
        </div>
        <h3 className={`splash-display uppercase mt-2 leading-tight ${primary ? "text-3xl md:text-4xl" : "text-2xl"}`}>
          {m.title}
        </h3>
        {m.kicker && (
          <div className="splash-script text-xl md:text-2xl text-[#4f8c19] mt-1">
            {m.kicker}
          </div>
        )}
        <p className="mt-3 text-[14px] md:text-[15px] text-[#0d100d]/80 leading-relaxed whitespace-pre-wrap">
          {m.body}
        </p>
        {m.location_label && (
          <div className="mt-3 inline-flex items-center gap-2 text-[13px] text-[#0d100d]/70 bg-[#76B82A]/10 border border-[#76B82A]/30 px-3 py-1.5">
            <MapPin size={13} className="text-[#4f8c19]" />
            <span>{m.location_label}</span>
          </div>
        )}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {me ? (
            <button onClick={onSubmit} className="btn-paper-light" data-testid={`mission-submit-${m.id}`}>
              <Camera size={14} /> Share the good
            </button>
          ) : (
            <Link to={`/login?next=/missions`} className="btn-paper-light">
              Sign in to submit <ArrowRight size={14} />
            </Link>
          )}
          {remaining !== null && (
            <span className="splash-mono text-[12px] text-[#0d100d]/55">
              {remaining > 0 ? `${remaining} spots left` : "All spots claimed"}
            </span>
          )}
          {m.completion_count > 0 && (
            <span className="inline-flex items-center gap-1 text-[12px] text-[#4f8c19]">
              <Heart size={11} /> {m.completion_count} hunter{m.completion_count === 1 ? "" : "s"} completed
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

/* ===================================================================== */

function ProofModal({ mission, settings, me, onClose }) {
  const [photo, setPhoto] = useState("");
  const [caption, setCaption] = useState("");
  const [coords, setCoords] = useState(null);
  const [busy, setBusy] = useState(false);
  const captionMin = settings?.submit_caption_min || 10;
  const captionMax = settings?.submit_caption_max || 600;
  const requireLocation = !!settings?.require_location;

  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, captured_at: new Date().toISOString() });
        toast.success("Location captured");
      },
      (err) => toast.error(err.message || "Couldn't get your location"),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) {
      toast.error("Please upload a selfie or proof photo.");
      return;
    }
    if (caption.length < captionMin) {
      toast.error(`Caption needs at least ${captionMin} characters.`);
      return;
    }
    if (requireLocation && !coords) {
      toast.error("This mission requires capturing your location. Tap 'Capture location'.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/missions/${mission.id}/proofs`, {
        photo_url: photo,
        caption: caption.trim(),
        lat: coords?.lat,
        lng: coords?.lng,
        captured_at: coords?.captured_at,
      });
      toast.success("Proof submitted! We'll review and award the badge soon.");
      onClose(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : (typeof detail === "string" ? detail : "Couldn't submit.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={() => onClose(false)}
      data-testid="proof-modal"
    >
      <form
        onSubmit={submit}
        className="splash-root bg-[#0d100d] border-2 border-[#76B82A]/40 max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 md:p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="splash-mono uppercase tracking-[0.22em] text-[10px] text-[#76B82A]">Share the good</div>
            <h3 className="splash-display text-2xl uppercase mt-1 text-[#f7f3e6]">{mission.title}</h3>
          </div>
          <button type="button" onClick={() => onClose(false)} className="text-[#f7f3e6]/55 hover:text-[#f7f3e6]"><X size={20} /></button>
        </div>
        <label className="block">
          <div className="splash-mono uppercase tracking-widest text-[10px] text-[#76B82A] mb-1.5">
            Selfie or photo of the moment *
          </div>
          <PhotoUploader value={photo} onChange={setPhoto} testid="proof-photo" />
        </label>
        <label className="block">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="splash-mono uppercase tracking-widest text-[10px] text-[#76B82A]">
              Tell the story *
            </span>
            <span className="text-[10px] text-[#f7f3e6]/45">{caption.length}/{captionMax}</span>
          </div>
          <textarea
            required
            minLength={captionMin}
            maxLength={captionMax}
            rows={4}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="lm-input"
            placeholder="What did you do? Where? Who'd you help? — your story might inspire someone else."
            data-testid="proof-caption"
          />
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={captureLocation} className="btn-loot-outline text-xs">
            <MapPin size={12} /> {coords ? "Location captured" : "Capture location"}
          </button>
          {coords && (
            <span className="splash-mono text-[11px] text-[#76B82A]">
              {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
            </span>
          )}
          {requireLocation && !coords && (
            <span className="splash-mono text-[10px] text-[#d7a62a]">required</span>
          )}
        </div>
        <button
          type="submit"
          disabled={busy}
          className="btn-loot-solid w-full justify-center"
          data-testid="proof-submit"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {busy ? "Sharing…" : "Share the good"}
        </button>
        <p className="text-[12px] text-[#f7f3e6]/55 text-center">
          We review every story. You'll get the Good Deed badge once approved — and your moment goes on the wall to inspire the next person.
        </p>
      </form>
    </div>
  );
}
